import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Close, History, Search } from "@material-ui/icons"
import React, { useCallback, useEffect, useState } from "react"
import type { FiltroLog, LogAlteracao } from "../../../services/logAlteracao"
import { obterHistoricoAlteracoes } from "../../../services/logAlteracao"
import { useSnackbar } from "../../../utils/notificacoes"

const TAMANHO_PAGINA = 50

// Rotulos amigaveis para os enums do backend (TipoEventoLog/AcaoLog/OrigemLog).
const LABEL_TIPO_EVENTO: Record<string, string> = {
    FORMA_PAGAMENTO: "Formas de pagamento",
    CADASTRO_ESTABELECIMENTO: "Cadastro da loja",
    PROMOCAO_FIDELIDADE: "Promoção de fidelidade",
}

const LABEL_ACAO: Record<string, string> = {
    CRIACAO: "Criação",
    ALTERACAO: "Alteração",
    EXCLUSAO: "Exclusão",
}

const LABEL_ORIGEM: Record<string, string> = {
    PAINEL: "Painel",
    API: "Painel (novo)",
}

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "1100px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    filterRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: "wrap",
    },
    origemChip: {
        backgroundColor: "#e3f2fd",
        color: "#1565c0",
        fontWeight: 600,
    },
    detalhesJson: {
        backgroundColor: "#f5f5f5",
        borderRadius: 8,
        padding: theme.spacing(2),
        fontFamily: "monospace",
        fontSize: "0.8rem",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxHeight: 320,
        overflow: "auto",
    },
}))

function formatarDataHora(valor: string | null): string {
    if (!valor) return "—"
    const data = new Date(valor)
    if (isNaN(data.getTime())) return valor
    return data.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}

function rotulo(mapa: Record<string, string>, valor: string | null): string {
    if (!valor) return "—"
    return mapa[valor] || valor
}

function detalhesFormatado(detalhes: unknown): string {
    if (detalhes == null) return "Sem detalhes."
    try {
        if (typeof detalhes === "string") {
            // Backend pode devolver a coluna JSON como string; tenta embelezar.
            return JSON.stringify(JSON.parse(detalhes), null, 2)
        }
        return JSON.stringify(detalhes, null, 2)
    } catch (_) {
        return String(detalhes)
    }
}

type DialogDetalhesProps = {
    log: LogAlteracao | null
    onClose: () => void
}

function DialogDetalhes({ log, onClose }: DialogDetalhesProps) {
    const classes = useStyles()
    if (!log) return null

    return (
        <Dialog open={!!log} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        Detalhes do registro #{log.id}
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <Close />
                    </IconButton>
                </Box>
                <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                    {rotulo(LABEL_TIPO_EVENTO, log.tipoEvento)} · {formatarDataHora(log.dataHora)}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2">
                        <strong>Usuário:</strong> {log.usuarioNome || "—"}
                        {log.usuarioEmail ? ` (${log.usuarioEmail})` : ""}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Ação:</strong> {rotulo(LABEL_ACAO, log.acao)} ·{" "}
                        <strong>Origem:</strong> {rotulo(LABEL_ORIGEM, log.origem)}
                    </Typography>
                    {log.ip ? (
                        <Typography variant="body2">
                            <strong>IP:</strong> {log.ip}
                        </Typography>
                    ) : null}
                    {log.descricao ? (
                        <Typography variant="body2" style={{ marginTop: 4 }}>
                            {log.descricao}
                        </Typography>
                    ) : null}
                </Box>

                <Divider style={{ marginBottom: 16 }} />

                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                    Detalhes
                </Typography>
                <div className={classes.detalhesJson}>{detalhesFormatado(log.detalhes)}</div>
            </DialogContent>
        </Dialog>
    )
}

export function TabelaHistoricoAlteracoes() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()

    const autorizacao = localStorage.getItem("chaveSistema") || ""

    const [tipoEvento, setTipoEvento] = useState<string>("")
    const [dataInicio, setDataInicio] = useState<string>("")
    const [dataFim, setDataFim] = useState<string>("")

    const [itens, setItens] = useState<LogAlteracao[]>([])
    const [pagina, setPagina] = useState<number>(0)
    const [temMais, setTemMais] = useState<boolean>(false)
    const [carregando, setCarregando] = useState<boolean>(false)

    const [logSelecionado, setLogSelecionado] = useState<LogAlteracao | null>(null)

    const carregar = useCallback(
        async (paginaAlvo: number, filtro: FiltroLog) => {
            if (!autorizacao) {
                enqueueSnackbar("Sessão expirada. Faça login novamente.", { variant: "error" })
                return
            }
            setCarregando(true)
            try {
                const resposta = await obterHistoricoAlteracoes(autorizacao, {
                    ...filtro,
                    pagina: paginaAlvo,
                    tamanho: TAMANHO_PAGINA,
                })
                if ("retornoErro" in resposta) {
                    enqueueSnackbar(
                        resposta.mensagem || "Não foi possível carregar o histórico.",
                        { variant: "error" }
                    )
                    return
                }
                // Primeira pagina substitui; paginas seguintes acumulam ("carregar mais").
                setItens(anteriores =>
                    paginaAlvo === 0 ? resposta.itens : [...anteriores, ...resposta.itens]
                )
                setPagina(resposta.pagina)
                setTemMais(resposta.temMais)
            } catch (error) {
                enqueueSnackbar("Erro ao carregar o histórico.", { variant: "error" })
            } finally {
                setCarregando(false)
            }
        },
        [autorizacao, enqueueSnackbar]
    )

    // Carrega a primeira pagina ao abrir a tela.
    useEffect(() => {
        carregar(0, {})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const filtroAtual = (): FiltroLog => ({
        tipoEvento: tipoEvento || undefined,
        dataInicio: dataInicio || undefined,
        dataFim: dataFim || undefined,
    })

    const handleBuscar = () => {
        if (dataInicio && dataFim && dataInicio > dataFim) {
            enqueueSnackbar("A data inicial não pode ser maior que a final.", { variant: "warning" })
            return
        }
        carregar(0, filtroAtual())
    }

    const handleCarregarMais = () => {
        carregar(pagina + 1, filtroAtual())
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box mb={3}>
                    <Typography variant="h5" className={classes.titleText}>
                        Histórico de Alterações
                    </Typography>
                    <Typography variant="body2" style={{ color: "#7f8c8d", marginTop: 4 }}>
                        Registro de quem alterou o quê no estabelecimento (formas de pagamento,
                        cadastro da loja, promoções e outros).
                    </Typography>
                </Box>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <History className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Alterações
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.filterRow}>
                            <TextField
                                label="Tipo de evento"
                                select
                                variant="outlined"
                                size="small"
                                value={tipoEvento}
                                onChange={e => setTipoEvento(e.target.value)}
                                style={{ minWidth: 200 }}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {Object.keys(LABEL_TIPO_EVENTO).map(chave => (
                                    <MenuItem key={chave} value={chave}>
                                        {LABEL_TIPO_EVENTO[chave]}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Início"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={dataInicio}
                                onChange={e => setDataInicio(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                style={{ minWidth: 160 }}
                            />
                            <TextField
                                label="Fim"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={dataFim}
                                onChange={e => setDataFim(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                style={{ minWidth: 160 }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Search />}
                                onClick={handleBuscar}
                                disabled={carregando}
                                style={{ borderRadius: 25 }}
                            >
                                Buscar
                            </Button>
                        </Box>

                        {itens.length === 0 ? (
                            <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                {carregando ? "Carregando…" : "Nenhuma alteração encontrada."}
                            </Typography>
                        ) : (
                            <>
                                <TableContainer style={{ overflowX: "auto" }}>
                                    <Table
                                        size="small"
                                        stickyHeader
                                        style={{ width: "100%", minWidth: 900 }}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ minWidth: 140 }}>
                                                    Data/Hora
                                                </TableCell>
                                                <TableCell style={{ minWidth: 150 }}>
                                                    Evento
                                                </TableCell>
                                                <TableCell style={{ minWidth: 100 }}>Ação</TableCell>
                                                <TableCell style={{ minWidth: 180 }}>
                                                    Usuário
                                                </TableCell>
                                                <TableCell style={{ minWidth: 110 }}>
                                                    Origem
                                                </TableCell>
                                                <TableCell style={{ minWidth: 240 }}>
                                                    Descrição
                                                </TableCell>
                                                <TableCell style={{ minWidth: 90 }} />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {itens.map(log => (
                                                <TableRow key={log.id} hover>
                                                    <TableCell>
                                                        {formatarDataHora(log.dataHora)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {rotulo(LABEL_TIPO_EVENTO, log.tipoEvento)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {rotulo(LABEL_ACAO, log.acao)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {log.usuarioNome || "—"}
                                                        {log.usuarioEmail ? (
                                                            <Typography
                                                                variant="caption"
                                                                display="block"
                                                                style={{ color: "#7f8c8d" }}
                                                            >
                                                                {log.usuarioEmail}
                                                            </Typography>
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={rotulo(LABEL_ORIGEM, log.origem)}
                                                            size="small"
                                                            className={classes.origemChip}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{log.descricao || "—"}</TableCell>
                                                    <TableCell>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => setLogSelecionado(log)}
                                                            style={{
                                                                borderRadius: 20,
                                                                fontSize: "0.7rem",
                                                            }}
                                                        >
                                                            Detalhes
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {temMais ? (
                                    <Box display="flex" justifyContent="center" mt={2}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={handleCarregarMais}
                                            disabled={carregando}
                                            style={{ borderRadius: 25 }}
                                        >
                                            {carregando ? "Carregando…" : "Carregar mais"}
                                        </Button>
                                    </Box>
                                ) : null}
                            </>
                        )}
                    </Box>
                </Paper>
            </Box>

            <DialogDetalhes log={logSelecionado} onClose={() => setLogSelecionado(null)} />
        </Box>
    )
}
